// src/components/DashEap.jsx
import React, { useState, useEffect } from "react";
import "./DashEap.css";

const formatarMoeda = (valor) => {
  if (valor == null || valor === "") return "—";
  const num = Number(valor);
  return isNaN(num) ? "—" : num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const extrairTexto = (item) => {
  if (!item) return "";
  return typeof item === "string" ? item : (item.name ?? item.nome ?? "");
};

function DashEap({ eapList, orcamentoTotal, fatorIncc }) {
  const [eapComValores, setEapComValores] = useState([]);
  const [blocosExpandidos, setBlocosExpandidos] = useState({});

  useEffect(() => {
    const agruparPorBloco = (items) => {
      const grupos = {};
      items.forEach((item) => {
        const bloco = item.bloco || "EAP Direto";
        if (!grupos[bloco]) {
          grupos[bloco] = [];
        }
        grupos[bloco].push(item);
      });
      return grupos;
    };

    const agrupados = agruparPorBloco(eapList || []);
    setEapComValores(agrupados);
    
    const expandidos = {};
    Object.keys(agrupados).forEach(bloco => {
      expandidos[bloco] = true;
    });
    setBlocosExpandidos(expandidos);
  }, [eapList]);

  const toggleBloco = (bloco) => {
    setBlocosExpandidos(prev => ({
      ...prev,
      [bloco]: !prev[bloco]
    }));
  };

  const calcularValorCorrigido = (valorBase) => {
    if (!fatorIncc || !valorBase) return valorBase;
    return valorBase * fatorIncc;
  };

  const calcularTotalBloco = (itens) => {
    let totalBase = 0;
    let totalCorrigido = 0;
    
    itens.forEach((item) => {
      const valor = Number(item.orcamento_base) || 0;
      totalBase += valor;
      totalCorrigido += calcularValorCorrigido(valor);
    });
    
    return { totalBase, totalCorrigido };
  };

  const totaisGerais = Object.values(eapComValores).reduce((acc, itens) => {
    const { totalBase, totalCorrigido } = calcularTotalBloco(itens);
    acc.totalBase += totalBase;
    acc.totalCorrigido += totalCorrigido;
    return acc;
  }, { totalBase: 0, totalCorrigido: 0 });

  const totalCorrigidoGeral = fatorIncc && orcamentoTotal ? orcamentoTotal * fatorIncc : orcamentoTotal || totaisGerais.totalCorrigido;

  return (
    <div className="dash-eap">
      <div className="dash-eap-header">
        <span className="dash-eap-title">PACOTES (EAP)</span>
        {fatorIncc && (
          <span className="dash-eap-incc-badge">
            INCC: {((fatorIncc - 1) * 100).toFixed(2)}%
          </span>
        )}
      </div>
      
      <div className="dash-eap-table-wrapper">
        <table className="dash-eap-table">
          <thead>
            <tr>
              <th className="dash-eap-col-nome">Pacote / Entregável</th>
              <th className="dash-eap-col-base">Base</th>
              <th className="dash-eap-col-corrigido">Corrigido</th>
            </tr>
          </thead>
          <tbody>
            <tr className="dash-eap-total-row">
              <td className="dash-eap-total-nome">
                <span className="dash-eap-total-label">Total do Projeto</span>
              </td>
              <td className="dash-eap-total-base">
                <span className="dash-eap-valor-base">{formatarMoeda(orcamentoTotal || totaisGerais.totalBase)}</span>
              </td>
              <td className="dash-eap-total-corrigido">
                <span className="dash-eap-valor-corrigido">{formatarMoeda(totalCorrigidoGeral)}</span>
              </td>
            </tr>
            
            {Object.entries(eapComValores).map(([bloco, itens]) => {
              const { totalBase, totalCorrigido } = calcularTotalBloco(itens);
              const isExpandido = blocosExpandidos[bloco] !== false;
              const itensVisiveis = isExpandido ? itens : [];
              
              return (
                <React.Fragment key={bloco}>
                  <tr 
                    className="dash-eap-bloco-row"
                    onClick={() => toggleBloco(bloco)}
                  >
                    <td className="dash-eap-bloco-nome">
                      <div className="dash-eap-bloco-header">
                        <span className="dash-eap-bloco-toggle">
                          {isExpandido ? '▼' : '▶'}
                        </span>
                        <span className="dash-eap-bloco-nome-text">{bloco}</span>
                        <span className="dash-eap-bloco-count">({itens.length})</span>
                      </div>
                    </td>
                    <td className="dash-eap-bloco-base">
                      <span className="dash-eap-valor-base">{formatarMoeda(totalBase)}</span>
                    </td>
                    <td className="dash-eap-bloco-corrigido">
                      <span className="dash-eap-valor-corrigido">{formatarMoeda(totalCorrigido)}</span>
                    </td>
                  </tr>
                  
                  {itensVisiveis.map((item, idx) => {
                    const valorBase = Number(item.orcamento_base) || 0;
                    const valorCorrigido = calcularValorCorrigido(valorBase);
                    
                    return (
                      <tr key={item.id ?? idx} className="dash-eap-item-row">
                        <td className="dash-eap-item-nome">
                          <span className="dash-eap-item-text">{extrairTexto(item)}</span>
                        </td>
                        <td className="dash-eap-item-base">
                          <span className="dash-eap-valor-base">{formatarMoeda(valorBase)}</span>
                        </td>
                        <td className="dash-eap-item-corrigido">
                          <span className="dash-eap-valor-corrigido">{formatarMoeda(valorCorrigido)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
            
            {Object.keys(eapComValores).length === 0 && (
              <tr>
                <td colSpan={3} className="dash-eap-empty">
                  Sem itens de EAP
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {Object.keys(eapComValores).length > 0 && (
        <div className="dash-eap-footer">
          <span className="dash-eap-count">
            {Object.values(eapComValores).reduce((acc, itens) => acc + itens.length, 0)} itens em {Object.keys(eapComValores).length} bloco{Object.keys(eapComValores).length > 1 ? 's' : ''}
          </span>
          {fatorIncc && (
            <span className="dash-eap-incc-info">Valores corrigidos pelo INCC</span>
          )}
        </div>
      )}
    </div>
  );
}

export default DashEap;